import { Divider, FormGroup, FormParagraph, List } from '@ksyos/design-system';
import type {
    IOrthCV1DiagnosticReportCustomControlDataBevindingen,
    IOrthCV1DiagnosticReportCustomControlDataWerkdiagnose,
} from '@common/validation/customControls/ophthalmology';
import { ConclusieDiagnoseOption } from '@common/enums/conclusieDiagnoseOption';
import { getDisplayName } from '@common/utils/getDisplayName';
import { OphthalmologyPoliciesEnum } from '@common/validation/customControls/ophthalmology';
import { translateKey } from '@frontend/translate';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { HeaderDivider } from '../../../../elements/common';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { RequestMessageContainer } from '../../../episodes/RequestMessageContainer';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { DisplayIcd10 } from '../../utils/v1/Icd10/DisplayIcd10';

const bevindingenLabels: { [key: string]: string } = {
    niet_te_beoordelen: 'Niet te beoordelen',
    geen_afwijkingen: 'Geen afwijkingen',
};

export const orthCV1DiagnosticReportWerkdiagnoseLabels: {
    [K in IOrthCV1DiagnosticReportCustomControlDataWerkdiagnose]: string;
} = {
    hypermetropie: 'Refractiestoornis: Hypermetropie',
    myopie: 'Refractiestoornis: Myopie',
    anisometropie: 'Refractiestoornis: Anisometropie',
    astigmatisme: 'Refractiestoornis: Astigmatisme',
    insufficientie_en_overmatige_convergentie: 'Insufficiëntie en overmatige convergentie',
    strabismus: 'Strabismus',
    amblyopie: 'Amblyopie',
    anders: 'Anders',
};

export function OrthCV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {Object.values(props.diagnosticReport.customControlData.bevindingen).some((value) => !!value) && (
                    <FormGroup label="Bevindingen">
                        <List>
                            {bevindingenOption('niet_te_beoordelen')}
                            {bevindingenOption('geen_afwijkingen')}
                            {bevindingenOption('andersText')}
                        </List>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.conclusie_diagnose && (
                    <FormGroup label="Werkdiagnose">
                        {props.diagnosticReport.customControlData.conclusie_diagnose !== ConclusieDiagnoseOption.ja && (
                            <FormParagraph>{props.diagnosticReport.customControlData.conclusie_diagnose}</FormParagraph>
                        )}
                        {werkdiagnoseOption(props.diagnosticReport.customControlData.werkdiagnose)}
                    </FormGroup>
                )}

                {props.diagnosticReport.diagnosis?.diagnosisDifferentialConsiderationIcd10a && (
                    <FormGroup label={translateKey('diagnosis.differentialDiagnosis')}>
                        <List>
                            {props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10a && (
                                <FormParagraph
                                    key={props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10a}
                                >
                                    <DisplayIcd10
                                        icd10Code={
                                            props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10a
                                        }
                                        thesaurusTermId={
                                            props.diagnosticReport.diagnosis.differentialConsiderationThesaurusTermIda
                                        }
                                    />
                                </FormParagraph>
                            )}
                            {props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10b && (
                                <FormParagraph
                                    key={props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10b}
                                >
                                    <DisplayIcd10
                                        icd10Code={
                                            props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10b
                                        }
                                        thesaurusTermId={
                                            props.diagnosticReport.diagnosis.differentialConsiderationThesaurusTermIdb
                                        }
                                    />
                                </FormParagraph>
                            )}
                            {props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10c && (
                                <FormParagraph
                                    key={props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10c}
                                >
                                    <DisplayIcd10
                                        icd10Code={
                                            props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10c
                                        }
                                        thesaurusTermId={
                                            props.diagnosticReport.diagnosis.differentialConsiderationThesaurusTermIdc
                                        }
                                    />
                                </FormParagraph>
                            )}
                        </List>
                    </FormGroup>
                )}

                <Divider />

                <HeaderDivider text="Advies" />

                {props.diagnosticReport.customControlData.beleid && (
                    <FormGroup label="Beleid">
                        <FormParagraph>{props.diagnosticReport.customControlData.beleid}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.beleid === OphthalmologyPoliciesEnum.anders && (
                    <FormGroup label="Toelichting">
                        <FormParagraph>{props.diagnosticReport.customControlData.beleid_anders}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.urgentie_oogarts && (
                    <FormGroup label="Urgentie">
                        <FormParagraph>{props.diagnosticReport.customControlData.urgentie_oogarts}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.urgentie_optometrist && (
                    <FormGroup label="Urgentie">
                        <FormParagraph>{props.diagnosticReport.customControlData.urgentie_optometrist}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.urgentie_anders && (
                    <FormGroup label="Toelichting">
                        <FormParagraph>{props.diagnosticReport.customControlData.urgentie_anders}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.termijn && (
                    <FormGroup label="Termijn">
                        <FormParagraph>{props.diagnosticReport.customControlData.termijn}</FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig && (
                    <FormGroup label="Bezoek aan specialist nu nodig?">
                        <FormParagraph>
                            {props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig}
                        </FormParagraph>
                    </FormGroup>
                )}

                {props.diagnosticReport.remark && (
                    <FormGroup label="Toelichting">
                        <FormParagraph>{props.diagnosticReport.remark}</FormParagraph>
                    </FormGroup>
                )}
                {props.process.requestMessages && props.process.requestMessages.length > 0 && (
                    <RequestMessageContainer requestMessages={props.process.requestMessages} />
                )}
                <DiagnosticReportReadButtons
                    {...props}
                    requestMessageToGP
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideConsultReport')}
                    labelMore={translateKey('diagnosticReport.showConsultReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    function bevindingenOption(name: keyof IOrthCV1DiagnosticReportCustomControlDataBevindingen) {
        const bevindingen = props.diagnosticReport.customControlData.bevindingen;
        const nameOd = name + '_od';
        const nameOs = name + '_os';

        return bevindingen[name] ? (
            <>
                {bevindingenLabels[name] ? (
                    <FormParagraph>{bevindingenLabels[name]}</FormParagraph>
                ) : (
                    <FormParagraph>{bevindingen[name]}</FormParagraph>
                )}
                {bevindingen[nameOd] ? <FormParagraph>OD</FormParagraph> : null}
                {bevindingen[nameOs] ? <FormParagraph>OS</FormParagraph> : null}
            </>
        ) : null;
    }

    function werkdiagnoseOption(name: IOrthCV1DiagnosticReportCustomControlDataWerkdiagnose) {
        if (
            name === 'anders' &&
            (props.diagnosticReport.diagnosis?.diagnosisConsiderationIcd10 ||
                props?.diagnosticReport.diagnosis?.considerationThesaurusTermId)
        ) {
            return (
                <FormParagraph>
                    <DisplayIcd10
                        icd10Code={props.diagnosticReport.diagnosis?.diagnosisConsiderationIcd10}
                        thesaurusTermId={props?.diagnosticReport.diagnosis?.considerationThesaurusTermId}
                    />
                </FormParagraph>
            );
        }

        return <FormParagraph key={name}>{orthCV1DiagnosticReportWerkdiagnoseLabels[name]}</FormParagraph>;
    }
}
